import React, { useMemo, useCallback } from "react";
import { Stack, RadioGroup, Radio, Text, Box } from "@chakra-ui/react";
import { Modal, ModalBody, Spinner, useTranslation } from "@familyzone/component-library";
import { FilterGuardianOptions, hasFilter } from "../../storez/GuardianStore";

interface ExportParentsModalProps {
  isOpen: boolean;
  loading: boolean;
  exported: boolean;
  filter: FilterGuardianOptions;
  onClose: () => void;
  executeDownload: (filter: FilterGuardianOptions) => void;
}

const ExportParentsModal: React.FC<ExportParentsModalProps> = ({
  isOpen,
  loading,
  exported,
  filter,
  onClose,
  executeDownload,
}: ExportParentsModalProps) => {
  const { t } = useTranslation();
  const isFiltering = useMemo(() => hasFilter(filter), [filter]);
  const [exportTypeValue, setExportTypeValue] = React.useState("");
  const getFilter = useCallback(
    (): FilterGuardianOptions => (isFiltering ? filter : { studentStatus: exportTypeValue }),
    [isFiltering, filter, exportTypeValue]
  );

  return (
    <Modal
      headerText={t(isFiltering ? "Export Filtered Parent Data CSV" : "Export Parent Data CSV")}
      isOpen={isOpen}
      size="md"
      onClose={onClose}
      primaryCTALabel={loading ? t("Exporting..") : !exported ? t("Export") : undefined}
      primaryCTADisabled={loading || exported}
      onPrimaryCTAClick={() => executeDownload(getFilter())}
      secondaryCTALabel={exported ? t("Close") : t("Cancel")}
      secondaryCTADisabled={loading}
      onSecondaryCTAClick={onClose}
    >
      <ModalBody>
        <>
          {!exported && !loading && !isFiltering && (
            <Box>
              <Box mb={"8px"}>
                <Text as="b">Export:</Text>
              </Box>
              <RadioGroup value={exportTypeValue} onChange={setExportTypeValue} data-testid="export-type-input">
                <Stack direction={"column"}>
                  <Radio value={""}>All parent connections</Radio>
                  <Radio value={"CONNECTED"}>Connected parents only</Radio>
                  <Radio value={"ASSIGNED"}>Non-connected parents only</Radio>
                </Stack>
              </RadioGroup>
            </Box>
          )}

          {!exported && !loading && isFiltering && (
            <Box>
              <Text>{t("Only parents associated to at least 1 student will be exported.")}</Text>
            </Box>
          )}

          {!exported && loading && (
            <Box my="sp24" textAlign="center" data-testid="loading-animation">
              <Spinner size="md" />
              <Text pt="sp16">{t("Large dataset may take up to a minute to export. Please stand by...")}</Text>
            </Box>
          )}

          {exported && (
            <Box my="sp24">
              <Text>{t("Parent data successfully exported. You should see a download prompt.")}</Text>
            </Box>
          )}
        </>
      </ModalBody>
    </Modal>
  );
};

export default ExportParentsModal;
